import { Address } from '@src/suite/common/data-models/complex-types/address';
import { LookupList } from '@src/suite/common/data-models/complex-types/lookup-list';
import { MultiValue } from '@src/suite/common/data-models/complex-types/multi-value';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { IPlaybookService } from '@src/suite/playbook/interfaces/i-playbook-service';
import { DateTime } from '@suite/common/data-models/complex-types/date-time';

import { Record as EnpsRecord } from './record';

export class StoryRecord extends EnpsRecord {
    public static playbookService: IPlaybookService;

    public initialize() {
        this.type = ObjectTypes.SCRIPT;
        super.initialize();
        this.setupRequiredFieldsGroups('Script');
    }

    protected getExcludedFields(): { [id: string]: boolean } {
        const excluded: { [id: string]: boolean } = {};
        excluded[PSTFieldNames.PSTTags] = true;
        excluded[PSTFieldNames.Title] = true;
        excluded[PSTFieldNames.Description] = true;
        excluded[PSTFieldNames.PSTDesk] = true;
        excluded[PSTFieldNames.PSTStoryStatus] = true;
        excluded[PSTFieldNames.PSTStoryOutputType] = true;
        excluded[PSTFieldNames.PSTStoryOwner] = true;
        excluded[PSTFieldNames.PSTExternalFiles] = true;
        excluded[PSTFieldNames.PSTApproved] = true;
        return excluded;
    }

    public get storySlug(): string {
        return this.getAs<string>(PSTFieldNames.Title);
    }
    public set storySlug(value: string) {
        this.set(PSTFieldNames.Title, value);
    }

    public get fullStorySlug(): string {
        const storySlug = this.storySlug;
        const containerTitle = this.getAs<string>(PSTFieldNames.ContainerTitle);
        if (containerTitle?.length > 0) {
            return `${storySlug} [${containerTitle}]`;
        }
        return storySlug || '';
    }

    public get parentGuid(): string {
        let parentGuid = this.getAs<string>('ParentGuid');
        if (!parentGuid) {
            // Extract parent guid from the path
            if (this.path) {
                parentGuid = this.path.parentCollectionGuid;
            }
        }
        return parentGuid;
    }
    public set parentGuid(value: string) {
        this.set('ParentGuid', value);
    }

    public get parentCategory(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentCategory, new MultiValue<string>());
    }
    public set parentCategory(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTParentCategory, value);
    }

    public get parentOwner(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentOwner, new MultiValue<string>());
    }
    public set parentOwner(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTParentOwner, value);
    }

    public get parentLocation(): Address {
        return this.getAs<Address>(PSTFieldNames.PSTParentLocation, new Address());
    }
    public set parentLocation(value: Address) {
        this.set(PSTFieldNames.PSTParentLocation, value);
    }

    public get parentType(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentType);
    }
    public set parentType(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTParentType, value);
    }

    public get text(): string {
        return this.getAs<string>('text');
    }
    public set text(value: string) {
        this.set('text', value);
    }

    public get editLocked(): boolean {
        return this.getAs<string>(PSTFieldNames.EditLocked, '0', true) === '1';
    }

    public get editLockedBy(): string {
        return this.getAs<string>(PSTFieldNames.EditLockedBy, null, true);
    }

    public get textCommands(): Record<string, string> {
        return this.getAs<Record<string, string>>('TextCommands', {}, true);
    }

    public set textCommands(commands: Record<string, string>) {
        this.set('TextCommands', commands);
    }
    public get storyType(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicType, new MultiValue<string>());
    }
    public set storyType(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTStoryOutputType, value);
    }

    public get pstApproved(): boolean {
        return this.getAs<boolean>(PSTFieldNames.PSTApproved);
    }
    public set pstApproved(value: boolean) {
        this.set(PSTFieldNames.PSTApproved, value);
    }

    public get department(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTDesk, new MultiValue<string>());
    }
    public set department(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTDesk, value);
    }

    public get fileDate(): DateTime {
        return this.getAs<DateTime>(PSTFieldNames.TargetPublicationDate);
    }
    public set fileDate(value: DateTime) {
        this.set(PSTFieldNames.TargetPublicationDate, value);
    }

    public get storyOwner(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTStoryOwner, new MultiValue<string>());
    }
    public set storyOwner(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTStoryOwner, value);
    }

    public get status(): string {
        return this.getAs<string>(PSTFieldNames.PSTTopicStatus);
    }
    public set status(value: string) {
        this.set(PSTFieldNames.PSTTopicStatus, value);
    }

    public get tags(): LookupList {
        return this.getAs<LookupList>(PSTFieldNames.PSTTags, new LookupList());
    }
    public set tags(value: LookupList) {
        this.set(PSTFieldNames.PSTTags, value);
    }

    public currentParentIndex: number = -1;

    public get getSecurityPrivsWrite(): boolean {
        const desk = this.department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (EnpsRecord.globalTablesService.securityPrivileges[val]) {
                    if (EnpsRecord.globalTablesService.securityPrivileges[val].story.write) {
                        ret = true;
                    }
                } else {
                    ret = EnpsRecord.globalTablesService.securityPrivileges[EnpsRecord.nomWebServicesJson.PSTGroup].story.write;
                }
            }
            return ret;
        }
        return EnpsRecord.globalTablesService.securityPrivileges[EnpsRecord.nomWebServicesJson.PSTGroup].story.write;
    }

    public getSecurityPrivsRead(): boolean {
        const desk = this.department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (EnpsRecord.globalTablesService.securityPrivileges[val]) {
                    if (EnpsRecord.globalTablesService.securityPrivileges[val].story.read) {
                        ret = true;
                    }
                } else {
                    ret = EnpsRecord.globalTablesService.securityPrivileges[EnpsRecord.nomWebServicesJson.PSTGroup].story.read;
                }
            }
            return ret;
        }
        return EnpsRecord.globalTablesService.securityPrivileges[EnpsRecord.nomWebServicesJson.PSTGroup].story.read;
    }
}
